import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UserCog, Phone, KeyRound } from "lucide-react";

export default function AccountSettings() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [conf, setConf] = useState("");
  const [savingP, setSavingP] = useState(false);
  const [savingPin, setSavingPin] = useState(false);

  useEffect(() => { api.get("/me").then((r) => { setMe(r.data); setName(r.data.name); setPhone(r.data.phone || ""); }); }, []);

  const saveProfile = async () => {
    if (!name.trim()) return toast.error("Name can't be empty");
    setSavingP(true);
    try { await api.put("/me", { name: name.trim(), phone }); toast.success("Details updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    setSavingP(false);
  };

  const savePin = async () => {
    if (!/^\d{4}$/.test(np)) return toast.error("New PIN must be 4 digits");
    if (np !== conf) return toast.error("New PINs don't match");
    setSavingPin(true);
    try {
      await api.put("/me/pin", { current_pin: cur, new_pin: np });
      toast.success("PIN changed 🔒"); setCur(""); setNp(""); setConf("");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't change PIN"); }
    setSavingPin(false);
  };

  if (!me) return <div className="p-6 text-slate-400">Loading…</div>;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-lg mx-auto" data-testid="account-settings">
      <div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tight text-slate-800 flex items-center gap-2"><UserCog strokeWidth={2.5} /> My account</h1>
        <p className="text-slate-500 text-sm capitalize">{me.role} · {me.org_name}</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-3">
        <h2 className="font-heading font-bold text-slate-800 flex items-center gap-1.5"><Phone size={16} strokeWidth={2.5} /> Your details</h2>
        <div><label className="text-xs font-semibold text-slate-600">Name</label><Input data-testid="acct-name" className="h-12 mt-1" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div><label className="text-xs font-semibold text-slate-600">Mobile number (used to sign in)</label><Input data-testid="acct-phone" type="tel" inputMode="numeric" className="h-12 mt-1 font-num" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
        {me.email && <div><label className="text-xs font-semibold text-slate-600">Email</label><Input className="h-12 mt-1" value={me.email} disabled /></div>}
        <Button data-testid="acct-save" onClick={saveProfile} disabled={savingP} className="btn-grad text-white rounded-full">{savingP ? "Saving…" : "Save details"}</Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-3">
        <h2 className="font-heading font-bold text-slate-800 flex items-center gap-1.5"><KeyRound size={16} strokeWidth={2.5} /> Change PIN</h2>
        <div><label className="text-xs font-semibold text-slate-600">Current PIN</label><Input data-testid="pin-current" type="password" inputMode="numeric" maxLength={4} className="h-12 mt-1 font-num tracking-widest" value={cur} onChange={(e) => setCur(e.target.value.replace(/\D/g, ""))} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs font-semibold text-slate-600">New PIN</label><Input data-testid="pin-new" type="password" inputMode="numeric" maxLength={4} className="h-12 mt-1 font-num tracking-widest" value={np} onChange={(e) => setNp(e.target.value.replace(/\D/g, ""))} /></div>
          <div><label className="text-xs font-semibold text-slate-600">Confirm</label><Input data-testid="pin-confirm" type="password" inputMode="numeric" maxLength={4} className="h-12 mt-1 font-num tracking-widest" value={conf} onChange={(e) => setConf(e.target.value.replace(/\D/g, ""))} /></div>
        </div>
        <Button data-testid="pin-save" onClick={savePin} disabled={savingPin} className="btn-grad text-white rounded-full">{savingPin ? "Saving…" : "Change PIN"}</Button>
      </div>
    </div>
  );
}
